'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, { Suspense, useState } from 'react';
import { encodePassphrase, generateRoomId, randomString } from '@/lib/client-utils';
import styles from '../styles/Home.module.css';

/** 本地日历日，格式 YYYYMMDD（与房间链接 query `date` 一致） */
function formatLocalDateYYYYMMDD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

function Tabs(props: React.PropsWithChildren<{}>) {
  const searchParams = useSearchParams();
  const tabIndex = searchParams?.get('tab') === 'custom' ? 1 : 0;

  const router = useRouter();
  function onTabSelected(index: number) {
    const tab = index === 1 ? 'custom' : 'demo';
    router.push(`/?tab=${tab}`);
  }

  let tabs = React.Children.map(props.children, (child, index) => {
    return (
      <button
        className="lk-button"
        onClick={() => {
          if (onTabSelected) {
            onTabSelected(index);
          }
        }}
        aria-pressed={tabIndex === index}
      >
        {/* @ts-ignore */}
        {child?.props.label}
      </button>
    );
  });

  return (
    <div className={styles.tabContainer}>
      <div className={styles.tabSelect}>{tabs}</div>
      {/* @ts-ignore */}
      {props.children[tabIndex]}
    </div>
  );
}

function DemoMeetingTab(props: { label: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validationStatus, setValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');

  // 从URL参数中获取roomName
  const roomNameFromUrl = searchParams?.get('roomName');

  // 手机号验证函数
  const validatePhoneNumber = async () => {
    if (!phoneNumber) {
      alert('请输入手机号码');
      return false;
    }

    // 简单验证：允许数字、加号、空格、连字符，长度在7-20之间
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    const digitsOnly = phoneNumber.replace(/[\s\-\(\)]/g, '');

    if (!phoneRegex.test(phoneNumber)) {
      alert('手机号格式不正确，只能包含数字、空格、+、-、()');
      return false;
    }

    if (digitsOnly.length < 7 || digitsOnly.length > 20) {
      alert('手机号长度不正确，应该在7-20位数字之间');
      return false;
    }

    setValidationStatus('validating');
    try {
      // 使用纯数字或原始格式进行API调用
      const phoneForApi = digitsOnly.startsWith('+') ? digitsOnly : digitsOnly.replace(/^\+/, '');
      const response = await fetch(`https://signature.shenfei-rec.com/api/check_signature/${phoneForApi}`);
      const data = await response.json();

      if (response.ok && data.signature) {
        setValidationStatus('valid');
        return true;
      } else {
        setValidationStatus('invalid');
        return false;
      }
    } catch (error) {
      console.error('验证手机号时出错:', error);
      setValidationStatus('idle');
      alert('网络错误，请重试');
      return false;
    }
  };

  const startMeeting = async () => {
    const isValid = await validatePhoneNumber();
    if (isValid) {
      // 将验证成功的手机号存储到localStorage
      localStorage.setItem('validatedPhoneNumber', phoneNumber);

      // 如果URL中有roomName参数，则跳转到指定的房间，否则创建新房间
      const targetRoomName = roomNameFromUrl || generateRoomId();
      const dateParam = formatLocalDateYYYYMMDD(new Date());
      const queryBase = `phoneNumber=${encodeURIComponent(phoneNumber)}&date=${dateParam}`;

      if (e2ee) {
        // query 必须在 # 之前，hash 仅承载端到端密钥
        router.push(
          `/rooms/${targetRoomName}?${queryBase}#${encodePassphrase(sharedPassphrase)}`,
        );
      } else {
        router.push(`/rooms/${targetRoomName}?${queryBase}`);
      }
    }
  };

  return (
    <div className={styles.tabContent} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <p style={{ margin: 20, width: 300, textAlign: 'center' }}>
        {roomNameFromUrl ? '验证手机号后进入房间' : '在线演示'}
      </p>

      {/* 手机号输入框 */}
      <div style={{ marginBottom: '1rem', width: '300px' }}>
        <input
          type="tel"
          value={phoneNumber}
          onChange={(e) => {
            setPhoneNumber(e.target.value);
            setValidationStatus('idle');
          }}
          placeholder="请输入手机号码"
          style={{
            width: '100%',
            padding: '10px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            boxSizing: 'border-box',
            fontSize: '16px'
          }}
        />
      </div>

      {/* 验证失败提示 */}
      {validationStatus === 'invalid' && (
        <div style={{ marginBottom: '1rem', color: 'red', width: '300px' }}>
          当前手机号需先签名
          <a
            href="https://signature.shenfei-rec.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'blue', marginLeft: '5px' }}
          >
            去签名
          </a>
        </div>
      )}

      {/* 开始会议按钮 */}
      <button
        style={{ marginTop: '1rem', width: '300px' }}
        className="lk-button"
        onClick={startMeeting}
        disabled={validationStatus === 'validating'}
      >
        {validationStatus === 'validating'
          ? '验证中...'
          : roomNameFromUrl
            ? '验证并进入房间'
            : '开始会议'
        }
      </button>

      <div style={{ visibility: "hidden", display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input
            id="use-e2ee"
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
          ></input>
          <label htmlFor="use-e2ee">端到端加密</label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <label htmlFor="passphrase">密码</label>
            <input
              id="passphrase"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function CustomConnectionTab(props: { label: string }) {
  const router = useRouter();

  const [e2ee, setE2ee] = useState(false);
  const [sharedPassphrase, setSharedPassphrase] = useState(randomString(64));

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const serverUrl = formData.get('serverUrl');
    const token = formData.get('token');
    if (e2ee) {
      router.push(
        `/custom/?liveKitUrl=${serverUrl}&token=${token}#${encodePassphrase(sharedPassphrase)}`,
      );
    } else {
      router.push(`/custom/?liveKitUrl=${serverUrl}&token=${token}`);
    }
  };
  return (
    <form className={styles.tabContent} onSubmit={onSubmit}>
      <p style={{ marginTop: 0 }}>
        Connect LiveKit Meet with a custom server using LiveKit Cloud or LiveKit Server.
      </p>
      <input
        id="serverUrl"
        name="serverUrl"
        type="url"
        placeholder="LiveKit Server URL: wss://*.livekit.cloud"
        required
      />
      <textarea
        id="token"
        name="token"
        placeholder="Token"
        required
        rows={5}
        style={{ padding: '1px 2px', fontSize: 'inherit', lineHeight: 'inherit' }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
          <input
            id="use-e2ee"
            type="checkbox"
            checked={e2ee}
            onChange={(ev) => setE2ee(ev.target.checked)}
          ></input>
          <label htmlFor="use-e2ee">Enable end-to-end encryption</label>
        </div>
        {e2ee && (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            <label htmlFor="passphrase">Passphrase</label>
            <input
              id="passphrase"
              type="password"
              value={sharedPassphrase}
              onChange={(ev) => setSharedPassphrase(ev.target.value)}
            />
          </div>
        )}
      </div>

      <hr
        style={{ width: '100%', borderColor: 'rgba(255, 255, 255, 0.15)', marginBlock: '1rem' }}
      />
      <button
        style={{ paddingInline: '1.25rem', width: '100%' }}
        className="lk-button"
        type="submit"
      >
        Connect
      </button>
    </form>
  );
}

export default function Page() {
  return (
    <>
      <main className={styles.main} data-lk-theme="default">
        <div className="header">
          <img src="/images/ai-4.jpeg" alt="深飞科技" width="400" height="200" />
          <h2>
            语音识别、语音合成、口语评测，以及数据服务{' '}
            <a href="https://shenfei-tek.com/index.html" rel="noopener">
              深飞科技
            </a>
            {/* ,{' '}
            <a href="https://livekit.io/cloud?ref=meet" rel="noopener">
              LiveKit&nbsp;Cloud
            </a>{' '}
            and Next.js. */}
          </h2>
        </div>
        <Suspense fallback="Loading">
          <DemoMeetingTab label="Demo" />
          {/* <Tabs>
            <DemoMeetingTab label="Demo" />
            <CustomConnectionTab label="Custom" />
          </Tabs> */}
        </Suspense>
      </main>
      <footer data-lk-theme="default">
        托管于{' '}
        <a href="https://shenfei-tek.com/index.html" rel="noopener">
          深飞科技
        </a>
      </footer>
    </>
  );
}
